import { CreateReturnPayload, CreateReturnResult, LookupResult, SuggestedEmails } from '../types';

/**
 * Klient proxy /api/v1/vratky (VratkyController). Business chyby chodí jako
 * HTTP 200 s {error_message} v datech; ApiError nastává jen u infra chyb
 * (502 BACKEND_UNAVAILABLE, rate limit, nevalidní odpověď).
 */

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly code: string,
        message: string,
        public readonly fields?: Record<string, string>,
    ) {
        super(message);
    }
}

async function call<T>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: {
            // 'XMLHttpRequest' kvůli Tracy isAjax() — v dev módu jinak Tracy echuje
            // deprecations do JSON odpovědi a rozbije parsování
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            ...(options.headers ?? {}),
        },
    });

    const body = await response.json().catch(() => null);

    if (!body || typeof body.ok !== 'boolean') {
        throw new ApiError(response.status, 'INVALID_RESPONSE', 'Unexpected server response');
    }

    if (!body.ok) {
        throw new ApiError(
            response.status,
            body.error?.code ?? 'UNKNOWN',
            body.error?.message ?? 'Unknown error',
            body.error?.fields,
        );
    }

    return body.data as T;
}

export function createVratkyApi(baseUrl: string) {
    return {
        lookup: (cislo: string, email: string, noOrderEmail: boolean) =>
            call<LookupResult>(
                `${baseUrl}/lookup?cislo=${encodeURIComponent(cislo)}&email=${encodeURIComponent(email)}&no_order_email=${noOrderEmail ? '1' : '0'}`,
            ),

        suggestEmails: (cislo: string) =>
            call<SuggestedEmails>(`${baseUrl}/suggest-email?cislo=${encodeURIComponent(cislo)}`),

        createReturn: (payload: CreateReturnPayload) =>
            call<CreateReturnResult>(`${baseUrl}/`, {
                method: 'POST',
                body: JSON.stringify(payload),
            }),
    };
}

export type VratkyApi = ReturnType<typeof createVratkyApi>;
